import clsx from 'clsx';
import type * as React from 'react';

import type {
    HiveBranchPresence,
    HiveDataSyncPresence,
    HiveWorkingBranch,
} from '@gitbook/hive-client';
import { useHivePresences } from '@gitbook/hive-react';

import { UserAvatarStack, type UserData } from '../Users';

/**
 * A list of participants on a Hive branch.
 */
export function RealtimeParticipants(props: {
    branch: HiveWorkingBranch;
    className?: string;
    onAvatarPress?: (participant: UserData) => void;
}): React.ReactElement | null {
    const { branch, onAvatarPress, className } = props;

    const rawParticipants = useHivePresences(branch);

    const participants = getUniqueUserParticipants(rawParticipants);

    // Tests are run in parallel and we don't want unpredictable realtime participants to show.
    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return null;
    }

    if (participants.length === 0) {
        return null;
    }

    const MAX_AVATARS_IN_STACK = 3;

    return (
        <div className={clsx('flex justify-end', className)}>
            <UserAvatarStack
                users={participants.map((participant) => ({
                    id: participant.data.userId,
                    pageId: participant.data.pageId,
                }))}
                // The `max` avatars shown will become much fewer once we implement a way to show the remaining non-shown avatars in e.g. a tooltip/palette
                max={MAX_AVATARS_IN_STACK}
                size="sm"
                realtime
                onAvatarPress={onAvatarPress}
            />
        </div>
    );
}

const getUniqueUserParticipants = (rawParticipants: HiveDataSyncPresence<HiveBranchPresence>[]) => {
    const seenUserIds = new Set<string>();

    return rawParticipants.filter((participant) => {
        const userId = participant.data.userId;

        // Check if userId is truthy and not already seen
        if (!seenUserIds.has(userId)) {
            seenUserIds.add(userId);
            return true;
        }

        return false;
    });
};
